import {
  buildSubAgentConfigurations,
  buildSubAgentName,
  buildSubAgentPayload,
  BEHAVIOR_EXECUTION_SELECTIVE,
  DEFAULT_ESCALATION_COOLDOWN_HOURS,
  DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL,
  DEFAULT_SESSION_CLOSE_POLICY,
  getExecutionPhase,
  mapProcessingFeedbackToForm,
  mapLongRunningAnalysisToForm,
} from '../Onboarding/constants';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isPersistedSubAgent(id) {
  return typeof id === 'string' && UUID_PATTERN.test(id);
}

export function mapAgentToForm(agent) {
  const configurations = agent.configurations || {};

  return {
    agentName: agent.name || '',
    toneOfVoice: agent.tone_of_voice || '',
    llmModelLineId: agent.llm_profile?.model_line?.id || '',
    llmEffortId: agent.llm_profile?.effort?.id || '',
    sessionTimeoutHours:
      configurations.session_timeout_hours != null
        ? String(configurations.session_timeout_hours)
        : '4',
    sessionClosePolicy:
      configurations.session_close_policy || DEFAULT_SESSION_CLOSE_POLICY,
    escalationCooldownHours:
      configurations.escalation_cooldown_hours != null
        ? String(configurations.escalation_cooldown_hours)
        : DEFAULT_ESCALATION_COOLDOWN_HOURS,
    knowledgeRelevanceMode: configurations.knowledge_relevance_mode || 'high',
    knowledgeFileButtonEnabled: configurations.knowledge_file_button_enabled !== false,
    interactiveOptionsEnabled: Boolean(configurations.interactive_options_enabled),
    interactiveListButtonLabel:
      configurations.interactive_list_button_label
      || (configurations.interactive_options_enabled
        ? DEFAULT_INTERACTIVE_LIST_BUTTON_LABEL
        : ''),
    visualOutputEnabled: Boolean(configurations.visual_output_enabled),
    visualOutputFormats: Array.isArray(configurations.visual_output_formats)
      ? configurations.visual_output_formats
      : ['png', 'jpg', 'pdf'],
    visualOutputStyleInstructions: configurations.visual_output_style_instructions || '',
    visualOutputAssets: configurations.visual_output_assets || {},
    processingFeedback: mapProcessingFeedbackToForm(configurations),
    requireRegisteredContact: Boolean(configurations.require_registered_contact),
    shareCrossAgentContext: Boolean(configurations.share_cross_agent_context),
    behaviorExecutionSelective:
      configurations.behavior_execution === BEHAVIOR_EXECUTION_SELECTIVE,
    messagePolishMode: configurations.message_polish?.mode || 'standard',
    responseDepth: configurations.response_depth === 'expert' ? 'expert' : 'chat',
    messageFormat: configurations.message_format === 'rich' ? 'rich' : 'simple',
    longRunningAnalysis: mapLongRunningAnalysisToForm(configurations),
    subAgents: [],
  };
}

export function mapSubAgentFromApi(subAgent) {
  const configurations = subAgent.configurations || {};
  const inheritLlmProfile =
    subAgent.llm_profile?.label === 'Herdar do agente' ||
    (!subAgent.llm_profile?.model_line && !subAgent.llm_profile?.effort);

  return {
    id: subAgent.id,
    name: subAgent.name || '',
    type: subAgent.type || 'onboarding',
    enabled: subAgent.enabled !== false,
    instructions: subAgent.instructions || '',
    executionPhase: subAgent.execution_phase || '',
    knowledgeBaseIds: subAgent.knowledge_base_ids || [],
    inheritLlmProfile,
    llmModelLineId: subAgent.llm_profile?.model_line?.id || '',
    llmEffortId: subAgent.llm_profile?.effort?.id || '',
    knowledgeRelevanceMode: configurations.knowledge_relevance_mode || '',
    knowledgeResponseMode: configurations.knowledge_response_mode || 'creative',
    knowledgeRetrievalMode: configurations.knowledge_retrieval_mode || 'focused',
    knowledgeMaxEvidences:
      configurations.knowledge_max_evidences != null
        ? String(configurations.knowledge_max_evidences)
        : configurations.knowledge_retrieval_mode === 'diverse'
          ? '0'
          : '2',
    guardrailAction: configurations.action || 'warn',
    escalationMessage: configurations.escalation_message || '',
    priority: subAgent.priority != null ? String(subAgent.priority) : '0',
    mcpBindings: Array.isArray(configurations.mcp_bindings)
      ? configurations.mcp_bindings.map((binding, index) => ({
          id: binding.mcp_server_id || `binding-${index}`,
          mcpServerId: binding.mcp_server_id || '',
          instructions: binding.instructions || '',
          contactScoped: Boolean(binding.contact_scoped),
        }))
      : [],
    maxToolIterations:
      configurations.max_tool_iterations != null
        ? String(configurations.max_tool_iterations)
        : '',
    mcpFollowupReuseEnabled: Boolean(configurations.mcp_followup_reuse?.enabled),
    mcpFollowupReuseTtlSeconds:
      configurations.mcp_followup_reuse?.ttl_seconds != null
        ? String(configurations.mcp_followup_reuse.ttl_seconds)
        : '3600',
    postResponseActionOnly: Boolean(configurations.post_response_action_only),
    consumeUpstreamSkillsEnabled: Boolean(
      configurations.consume_upstream_skills?.enabled
    ),
    consumeUpstreamSkills: Array.isArray(
      configurations.consume_upstream_skills?.skills
    )
      ? configurations.consume_upstream_skills.skills
      : [],
    completionWebhookEnabled: Boolean(
      configurations.completion_webhook?.url ||
        configurations.completion_webhook?.edge_endpoint_ref
    ),
    completionWebhookDelivery: configurations.completion_webhook?.edge_endpoint_ref
      ? 'edge'
      : 'direct',
    completionWebhookEdgeRef:
      configurations.completion_webhook?.edge_endpoint_ref || '',
    completionWebhookUrl: configurations.completion_webhook?.url || '',
    completionWebhookMethod: configurations.completion_webhook?.method || 'POST',
    completionWebhookHeaders: configurations.completion_webhook?.headers
      ? JSON.stringify(configurations.completion_webhook.headers, null, 2)
      : '',
    mcpFieldPersistenceEnabled: Boolean(
      configurations.mcp_field_persistence?.enabled
    ),
    mcpFieldPersistenceFields: Array.isArray(
      configurations.mcp_field_persistence?.fields
    )
      ? configurations.mcp_field_persistence.fields.join('\n')
      : '',
  };
}

export function buildSubAgentUpdatePayload(subAgent, index, subAgents) {
  const payload = {
    type: subAgent.type,
    name: subAgent.name || buildSubAgentName(subAgent.type, subAgents, index),
    enabled: subAgent.enabled !== false,
    instructions: subAgent.instructions,
    configurations: buildSubAgentConfigurations(subAgent),
    execution_phase: getExecutionPhase(subAgent),
  };

  if (subAgent.inheritLlmProfile) {
    payload.inherit_llm_profile = true;
  } else {
    if (subAgent.llmModelLineId) {
      payload.llm_model_line_id = subAgent.llmModelLineId;
    }
    if (subAgent.llmEffortId) {
      payload.llm_effort_id = subAgent.llmEffortId;
    }
  }

  const priority = parseInt(subAgent.priority, 10);
  if (!Number.isNaN(priority)) {
    payload.priority = priority;
  }

  if (subAgent.knowledgeBaseIds?.length) {
    payload.knowledge_base_ids = subAgent.knowledgeBaseIds;
  } else {
    payload.knowledge_base_ids = [];
  }

  return payload;
}

export { buildSubAgentPayload };
